import { Component, DestroyRef, effect, inject, signal } from '@angular/core';
import { LoadingStore } from '../../../core/loading/store/loading.store';

/** Espera mínima antes de bloquear a tela: respostas rápidas mostram só a barra, sem piscar a camada. */
export const ATRASO_BLOQUEIO_MS = 400;

/**
 * Indicador global de carregamento, alimentado pelo `loadingInterceptor` (requisições HTTP em andamento).
 * Uma barra fina no topo aparece em qualquer requisição; se a espera passar de {@link ATRASO_BLOQUEIO_MS},
 * uma camada com spinner cobre a tela e impede cliques repetidos até a resposta chegar.
 */
@Component({
  selector: 'app-carregamento-global',
  standalone: true,
  template: `
    @if (carregando.loading()) {
      <div class="barra" role="progressbar" aria-label="Carregando"><span></span></div>
    }
    @if (bloqueado()) {
      <div class="camada" role="status" aria-live="polite">
        <div class="caixa">
          <span class="spinner" aria-hidden="true"></span>
          <span>Carregando...</span>
        </div>
      </div>
    }
  `,
  styles: [`
    @import 'constants';
    @import 'colors';

    .barra {
      position: fixed;
      top: 0;
      left: 0;
      right: 0;
      z-index: 2000;
      height: 3px;
      overflow: hidden;
      background: rgba($primary-color, 0.2);

      span {
        position: absolute;
        inset: 0 auto 0 0;
        width: 40%;
        background: $primary-color;
        animation: deslizar 1.1s ease-in-out infinite;
      }
    }

    .camada {
      position: fixed;
      inset: 0;
      z-index: 1999;
      display: grid;
      place-items: center;
      background: rgba($white-color-1, 0.55);
      cursor: progress;
    }

    .caixa {
      display: flex;
      align-items: center;
      gap: 1.5 * $base-spacing;
      padding: 1.5 * $base-spacing 3 * $base-spacing;
      border: 1px solid $cartao-borda;
      border-radius: $base-border-radius;
      background: $white-color-1;
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);
      color: $label-color;
    }

    .spinner {
      width: 20px;
      height: 20px;
      border: 2px solid rgba($primary-color, 0.25);
      border-top-color: $primary-color;
      border-radius: 50%;
      animation: girar 0.8s linear infinite;
    }

    @keyframes deslizar {
      from { transform: translateX(-100%); }
      to { transform: translateX(250%); }
    }

    @keyframes girar {
      to { transform: rotate(360deg); }
    }

    @media (prefers-reduced-motion: reduce) {
      .barra span { animation: none; width: 100%; opacity: 0.6; }
      .spinner { animation-duration: 2s; }
    }
  `]
})
export class CarregamentoGlobalComponent {
  protected readonly carregando = inject(LoadingStore);
  protected readonly bloqueado = signal(false);
  private temporizador: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    effect(() => {
      const ativo = this.carregando.loading();
      this.cancelar();
      if (ativo) {
        this.temporizador = setTimeout(() => this.bloqueado.set(true), ATRASO_BLOQUEIO_MS);
      } else {
        this.bloqueado.set(false);
      }
    });
    inject(DestroyRef).onDestroy(() => this.cancelar());
  }

  private cancelar(): void {
    if (this.temporizador !== null) {
      clearTimeout(this.temporizador);
      this.temporizador = null;
    }
  }
}
